import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { Bar } from 'react-chartjs-2';
import { ChartBar, CurrencyEur } from '@phosphor-icons/react';
import { formatCurrency } from 'shared/utils/currency';
import { AcquisitionFinancialsSummary } from '../types/statistics.types';
import { COMMON_TOOLTIP } from './chartConfig';
import { MetricInfo } from './MetricInfo';
import './chartConfig';

interface Props {
  data?: AcquisitionFinancialsSummary;
  loading: boolean;
}

const formatPct = (n: number) => `${(n * 100).toFixed(2)}%`;

export const FinancialsCard = ({ data, loading }: Props) => {
  if (loading || !data) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <ChartBar className="h-5 w-5 text-moss-primary" weight="duotone" />
            <Skeleton className="h-5 w-40" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  const yieldData = {
    labels: ['Worst case', 'Actueel', 'Best case'],
    datasets: [
      {
        label: 'Gem. nettorendement',
        data: [
          data.avgYieldWorst * 100,
          data.avgYieldActual * 100,
          data.avgYieldBest * 100,
        ],
        backgroundColor: ['#F43F5ECC', '#5B7304CC', '#22C55ECC'],
        borderColor: ['#F43F5E', '#5B7304', '#22C55E'],
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const stats = [
    {
      label: 'Gem. vraagprijs',
      value: formatCurrency(data.avgGrossAskingPrice),
    },
    {
      label: 'Gem. biedprijs',
      value: formatCurrency(data.avgBidPrice),
    },
    {
      label: 'Pipeline volume',
      value: formatCurrency(data.totalPipelineVolume),
    },
    {
      label: 'Volume gewonnen deals',
      value: formatCurrency(data.totalDealVolume),
    },
  ];

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <CurrencyEur className="h-5 w-5 text-moss-primary" weight="duotone" />
          <CardTitle className="text-lg">Financiële metrics</CardTitle>
          <MetricInfo
            description={
              <div className="space-y-1">
                <p>
                  Gemiddelden over <em>alle</em> dossiers met ingevulde waarde
                  (niet enkel deals).
                </p>
                <p>
                  <strong>Worst / Actueel / Best:</strong> bandbreedte van het
                  netto-rendement op basis van Excel-scenario's per dossier.
                </p>
                <p>
                  <strong>Pipeline volume:</strong> Σ bod (of vraagprijs als geen
                  bod) over niet-afgewezen dossiers.
                </p>
                <p>
                  <strong>Volume gewonnen deals:</strong> Σ bod over dossiers in
                  fase "Deal!".
                </p>
              </div>
            }
          />
        </div>
        <p className="text-xs text-neutral-secondary-mid">
          Yield-bandbreedte over alle dossiers met biedprijs en transactievolumes
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-md border border-neutral-100 bg-neutral-50/60 px-3 py-2"
            >
              <p className="text-xs text-neutral-secondary-mid">{s.label}</p>
              <p className="text-base font-semibold text-neutral-primary">
                {s.value}
              </p>
            </div>
          ))}
        </div>

        <div className="h-[220px]">
          <Bar
            data={yieldData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  ...COMMON_TOOLTIP,
                  callbacks: {
                    label: (ctx: any) => `${ctx.parsed.y.toFixed(2)}%`,
                  },
                },
              },
              scales: {
                x: { grid: { display: false } },
                y: {
                  beginAtZero: true,
                  grid: { color: 'rgba(49, 64, 59, 0.1)' },
                  ticks: {
                    callback: (v: any) => `${v}%`,
                  },
                },
              },
            }}
          />
        </div>

        <div className="flex flex-wrap gap-3 text-xs text-neutral-secondary-mid">
          <span>
            Worst:{' '}
            <span className="font-medium text-neutral-primary">
              {formatPct(data.avgYieldWorst)}
            </span>
          </span>
          <span>
            Actueel:{' '}
            <span className="font-medium text-moss-primary">
              {formatPct(data.avgYieldActual)}
            </span>
          </span>
          <span>
            Best:{' '}
            <span className="font-medium text-neutral-primary">
              {formatPct(data.avgYieldBest)}
            </span>
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
